import { keepPreviousData, useQuery } from '@tanstack/react-query';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { SearchParamsPagination } from 'src/components/Pagination';
import { Scroller } from 'src/components/Scroller';
import { SearchParamSearchTextField } from 'src/components/SearchTextField';
import { SectionToolbar } from 'src/components/SectionToolbar';
import { StickyCard } from 'src/components/StickyCard';
import { DataTable } from 'src/components/Table/DataTable';
import { Typography } from 'src/components/Typography';
import { fetchEvents } from 'src/models/event/event';
import { getUserDisplayName } from 'src/models/user/utils';
import { ErrorDisplay } from 'src/shared/ErrorDisplay';
import { DateRangeFilter } from 'src/shared/Filters/DateRangeFilter';
import { Filters } from 'src/shared/Filters/Filters';
import { useDreamcoatPageTitle } from 'src/shared/dreamcoat';
import { formatFullDateAndTime } from 'src/utils/dateFormatter';
import { useListSearchParams } from 'src/utils/useListSearchParams';

import { EventDescription } from './EventDescription';

import type { Event } from 'src/models/event/_types';
import type { User } from 'src/models/user/_types';

const ITEMS_PER_PAGE = 10;

type EventsTableProps = {
  user?: User;
};

const EventsTable: React.FC<EventsTableProps> = ({ user }) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'clients.eventsPage',
  });

  const stickyCardTitle = useDreamcoatPageTitle('actionLog', t('events'));
  const eventColumnLabel = useDreamcoatPageTitle(
    'actionLogEventColumn',
    t('event')
  );
  const userDisplayName = user ? getUserDisplayName(user) : '';
  const stickyCardSubtitle = useDreamcoatPageTitle(
    'actionLogFor',
    t('actionLogFor', { name: userDisplayName }),
    { name: userDisplayName }
  );

  const {
    params,
    filterParams,
    updateParam,
    updateFilterParam,
    getFilterParamValue,
    resetFilterParams,
  } = useListSearchParams({
    paramPrefix: 'event_',
  });

  const { page, perPage } = params;

  const { data, isLoading, isPending, error } = useQuery({
    queryKey: [
      'events',
      user?.id,
      page,
      perPage,
      params.search,
      filterParams.date,
    ],
    queryFn: () =>
      fetchEvents({
        page,
        perPage: perPage || ITEMS_PER_PAGE,
        userId: user?.id,
        search: params.search,
        startDate: filterParams.date?.start,
        endDate: filterParams.date?.end,
      }),
    enabled: !!user?.id,
    placeholderData: keepPreviousData,
  });

  if (error) {
    return (
      <ErrorDisplay
        title={t('errorLoadingEvents')}
        errorMessage={(error as Error).message || error}
      />
    );
  }

  const columns = [
    {
      id: 'event',
      label: eventColumnLabel,
      maxWidth: '300px',
      format: (value: any, row: Event) => (
        <Box sx={{ whiteSpace: 'wrap' }}>
          <Typography variant="body1">
            <EventDescription event={row} />
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('on', { time: formatFullDateAndTime(row.time) })}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'person',
      label: t('person'),
      format: (value: any, row: Event) => {
        const person = row.user || row.onBehalfOf;
        const impersonatorInfo = row.impersonator
          ? ` (performed by ${row.impersonator})`
          : '';
        return t('actor', { person, impersonatorInfo });
      },
    },
  ];

  const filters = [
    {
      field: 'date',
      label: t('date'),
      Component: DateRangeFilter,
    },
  ];

  if (!user) {
    return null;
  }

  return (
    <>
      <SectionToolbar sx={{ pb: 3, pt: 0, px: 0 }}>
        <SearchParamSearchTextField
          params={params}
          updateParam={updateParam}
          sx={{ width: '100%' }}
        />
      </SectionToolbar>
      <SectionToolbar sx={{ pb: 3, pt: 0, px: 0 }}>
        <Scroller
          direction="row"
          spacing={2}
          hideScrollbar
          sx={{ mx: -2, px: 2, boxSizing: 'content-box' }}
        >
          <Filters
            filters={filters}
            params={params}
            updateParam={updateParam}
            updateFilterParam={updateFilterParam}
            getFilterParamValue={getFilterParamValue}
            resetFilterParams={resetFilterParams}
          />
        </Scroller>
      </SectionToolbar>
      <StickyCard
        title={stickyCardTitle}
        contentSx={{ p: 0 }}
        subtitle={stickyCardSubtitle}
      >
        <DataTable
          columns={columns}
          rows={data?.events || []}
          loading={isLoading || isPending}
          itemsPerPage={ITEMS_PER_PAGE}
          totalItems={data?.total}
          noResultsText={t('noEventsFound')}
        />
        <SearchParamsPagination
          itemsPerPage={ITEMS_PER_PAGE}
          total={data?.total || 0}
          params={params}
          updateParam={updateParam}
          sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}
        />
      </StickyCard>
    </>
  );
};

export default EventsTable;
